import React, { useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  getShips,
  getLpSuppliers,
  addShip,
  updateShip,
  deleteShip,
} from '../../../../features/master-data';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import { StorageKey } from '../../../../shared/auth/DataService';
import MultiSelectColumnFilter from '../../../../shared/components/datagrid/MultiSelectColumnFilter';
import useSnackbar from '../../../../shared/hooks/useSnackbar';
import useRowEditing from '../../../../shared/hooks/useRowEditing';
import getActionsColumn from '../../../../shared/components/datagrid/getActionsColumn';
import AddRowToolbar from '../../../../shared/components/datagrid/AddRowToolbar';
import { scrollToNewRow } from '../../../../shared/utils/scrollToNewRow';

const ShipTable = () => {
  const [ships, setShips] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nextId, setNextId] = useState(1);
  const { showSnackbar, snackbarProps } = useSnackbar();
  const apiRef = useGridApiRef();

  const [filterModel, setFilterModel] = useState(() => {
    const saved = localStorage.getItem('shipTableFilter');
    return saved ? JSON.parse(saved) : { items: [] };
  });

  useEffect(() => {
    localStorage.setItem('shipTableFilter', JSON.stringify(filterModel));
  }, [filterModel]);

  const {
    rowModesModel,
    handleRowEditStop,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    startEditingRow,
    // ShipTable uses imo as the row ID, so we pass a custom getRowId to useRowEditing
  } = useRowEditing({
    rows: ships,
    setRows: setShips,
    getRowId: (row) => row.imo,
  });

  const fetchShips = async () => {
    try {
      const shipsResponse = await getShips();
      const shipsWithSupplier = shipsResponse.map((ship) => ({
        ...ship,
        supplier:
          ship.supplier?.supplierNo && ship.supplier?.supplierName
            ? {
                supplierNo: ship.supplier.supplierNo,
                supplierName: ship.supplier.supplierName,
              }
            : null,
      }));
      setShips(shipsWithSupplier);
      setNextId(
        shipsWithSupplier.length
          ? Math.max(...shipsWithSupplier.map((s) => s.imo)) + 1
          : 1
      );
    } catch (error) {
      console.error('Error fetching ships:', error);
      showSnackbar('Error fetching ships.', 'error');
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliers(await getLpSuppliers({ isShippingCompany: true }));
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      showSnackbar('Error fetching suppliers.', 'error');
    }
  };

  useEffect(() => {
    fetchShips();
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteClick = (imo) => async () => {
    try {
      await deleteShip(imo);
      await fetchShips();
      showSnackbar('Ship deleted successfully!');
    } catch (error) {
      console.error('Error deleting ship:', error);
      showSnackbar('Error deleting ship.', 'error');
    }
  };

  const processRowUpdate = async (newRow) => {
    const imoDigits = String(newRow.imo || '').replace(/\D/g, '');
    if (imoDigits.length !== 7) {
      showSnackbar('IMO must be exactly 7 digits.', 'error');
      throw new Error('IMO must be exactly 7 digits.');
    }
    const imoWithPrefix = `IMO${imoDigits}`;

    try {
      const emea = localStorage.getItem(StorageKey.userInfo_sub);
      if (!emea) throw new Error('User EMEA not found in localStorage');
      const updatedRow = {
        ...newRow,
        imo: imoWithPrefix,
        lastUpdatedBy: { emea },
      };

      if (newRow.isNew) {
        await addShip(updatedRow);
        showSnackbar('Ship added successfully!');
      } else {
        await updateShip(updatedRow.imo, updatedRow);
        showSnackbar('Ship updated successfully!');
      }
      await fetchShips();
    } catch (error) {
      console.error('Error saving ship:', error);
      showSnackbar('Error saving ship.', 'error');
      if (newRow.isNew) {
        setShips((prev) => prev.filter((row) => row.imo !== newRow.imo));
      }
      throw error;
    }
    return { ...newRow, imo: imoWithPrefix, isNew: false };
  };

  const handleAddClick = () => {
    const emea = localStorage.getItem(StorageKey.userInfo_sub);
    if (!emea) {
      showSnackbar('Error fetching user.', 'error');
      return;
    }
    const newId = nextId;
    setNextId(newId + 1);
    const newImo = newId.toString();
    setShips((oldRows) => [
      ...oldRows,
      {
        imo: newImo,
        irs: '',
        name: '',
        capacity: null,
        supplier: null,
        lastUpdatedBy: { emea },
        isNew: true,
      },
    ]);
    startEditingRow(newImo, 'imo');
    // Clear any active filters so the new row is visible, then scroll to it
    setFilterModel({ items: [] });
    scrollToNewRow(apiRef, ships.length + 1);
  };

  const shipColumns = [
    {
      field: 'imo',
      headerName: 'IMO',
      width: 150,
      editable: true,
      renderEditCell: (params) => {
        const digitsOnly = String(params.value || '').replace(/\D/g, '');
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
              px: 1,
            }}
          >
            <span style={{ fontWeight: 500, lineHeight: 1, marginRight: 0 }}>
              IMO
            </span>
            <TextField
              value={digitsOnly}
              onChange={(e) => {
                const input = e.target.value.replace(/\D/g, '').slice(0, 7);
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'imo',
                  value: input,
                });
              }}
              inputProps={{
                maxLength: 7,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { padding: 0 },
              }}
              placeholder='0000000'
              size='small'
              variant='standard'
              sx={{ flex: 1 }}
            />
          </Box>
        );
      },
    },
    { field: 'irs', headerName: 'IRS', width: 150, editable: true },
    { field: 'name', headerName: 'Ship Name', width: 300, editable: true },
    {
      field: 'capacity',
      headerName: 'Capacity',
      type: 'number',
      width: 150,
      editable: true,
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      width: 250,
      editable: true,
      filterable: true,
      renderCell: (params) =>
        params?.row?.supplier?.supplierNo && params.row.supplier.supplierName
          ? `${params.row.supplier.supplierNo} - ${params.row.supplier.supplierName}`
          : 'N/A',
      filterOperators: [
        {
          label: 'is any of',
          value: 'isAnyOf',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) return null;
            return (params) => {
              const supplierNo = params?.supplierNo;
              if (!supplierNo) return false;
              return filterItem.value.includes(supplierNo);
            };
          },
          InputComponent: (props) => {
            const supplierOptions = [
              ...new Map(
                ships
                  .filter(
                    (s) => s.supplier?.supplierNo && s.supplier?.supplierName
                  )
                  .map((s) => [
                    s.supplier.supplierNo,
                    {
                      value: s.supplier.supplierNo,
                      label: `${s.supplier.supplierNo} - ${s.supplier.supplierName}`,
                    },
                  ])
              ).values(),
            ].sort((a, b) => a.label.localeCompare(b.label));
            return (
              <MultiSelectColumnFilter
                item={props.item}
                applyValue={props.applyValue}
                options={supplierOptions}
              />
            );
          },
        },
        {
          label: 'contains',
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            const needle = filterItem.value.toString().toLowerCase();
            return (params) => {
              const haystack =
                `${params?.supplierNo || ''} ${params?.supplierName || ''}`.toLowerCase();
              return haystack.includes(needle);
            };
          },
        },
      ],
      sortComparator: (v1, v2) => {
        const noCompare = (v1?.supplierNo?.toString() || '').localeCompare(
          v2?.supplierNo?.toString() || ''
        );
        return noCompare !== 0
          ? noCompare
          : (v1?.supplierName || '').localeCompare(v2?.supplierName || '');
      },
      renderEditCell: (params) => (
        <Autocomplete
          options={suppliers}
          getOptionLabel={(option) =>
            option?.supplierNo && option?.supplierName
              ? `${option.supplierNo} - ${option.supplierName}`
              : 'N/A'
          }
          value={params?.row?.supplier || null}
          isOptionEqualToValue={(option, value) =>
            value
              ? option.supplierNo === value.supplierNo &&
                option.supplierName === value.supplierName
              : false
          }
          onChange={(_, newValue) => {
            params.api.setEditCellValue({
              id: params.id,
              field: 'supplier',
              value: newValue || null,
            });
          }}
          renderInput={(inputProps) => (
            <TextField {...inputProps} label='Supplier' sx={{ width: 230 }} />
          )}
        />
      ),
    },
    getActionsColumn({
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
    }),
  ];

  return (
    <Box height='50vh'>
      <DataGridPro
        apiRef={apiRef}
        pagination
        rows={ships}
        columns={shipColumns}
        getRowId={(row) => row.imo}
        editMode='row'
        // IMO is editable only for new rows; isCellEditable prevents editing on existing rows
        isCellEditable={(params) =>
          params.field !== 'imo' || !!params.row.isNew
        }
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) =>
          console.error('Row update error:', error)
        }
        filterMode='client'
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        slots={{ toolbar: AddRowToolbar }}
        slotProps={{ toolbar: { label: 'Add Ship', onAdd: handleAddClick } }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          columns: { columnVisibilityModel: { imo: true } },
        }}
      />
      <ExpSnackbarAlerts {...snackbarProps} />
    </Box>
  );
};

export default ShipTable;
